// ── Firebase Configuration ────────────────────────────────────────────────────
import { initializeApp } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithCredential,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { Capacitor } from "@capacitor/core";
import { GoogleAuth } from "@codetrix-studio/capacitor-google-auth";

const firebaseConfig = {
  apiKey: "AIzaSyBelcJD3zcnyfvq_RrwBQo-dAZ5WWu4bjo",
  authDomain: "samajh-9b80d.firebaseapp.com",
  projectId: "samajh-9b80d",
  storageBucket: "samajh-9b80d.firebasestorage.app",
  messagingSenderId: "884231999594",
  appId: "1:884231999594:web:4a41f675df77d9e6f6ba13"
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);

const googleProvider = new GoogleAuthProvider();

// Initialize Google Auth plugin for Capacitor (must happen before signIn)
if (Capacitor.isNativePlatform()) {
  GoogleAuth.initialize({
    scopes: ["profile", "email"],
    grantOfflineAccess: true,
  });
}

// ── Auth functions ───────────────────────────────────────────────────────────

export async function signInWithGoogle() {
  if (Capacitor.isNativePlatform()) {
    // Native Google Sign-In — no popups, no redirects, no WebView issues
    try {
      const googleUser = await GoogleAuth.signIn();
      if (!googleUser?.authentication?.idToken) {
        throw new Error("No ID token returned from native Google Sign-In");
      }
      const credential = GoogleAuthProvider.credential(googleUser.authentication.idToken);
      const result = await signInWithCredential(auth, credential);
      return result.user;
    } catch (err) {
      console.error("Native Google Sign-In failed:", err);
      throw err;
    }
  }

  // Web browser flow — popup with redirect fallback
  try {
    const result = await signInWithPopup(auth, googleProvider);
    return result.user;
  } catch (err) {
    if (err.code === "auth/popup-blocked" || err.code === "auth/popup-closed-by-user" || err.code === "auth/cancelled-popup-request" || err.code === "auth/internal-error") {
      await signInWithRedirect(auth, googleProvider);
      return null;
    }
    throw err;
  }
}

// Handle redirect result on page load — returns user or null
export async function handleRedirectSignIn() {
  try {
    const result = await getRedirectResult(auth);
    if (result?.user) return result.user;
    return null;
  } catch (e) {
    console.warn("Redirect result error:", e);
    return null;
  }
}

export async function signInWithEmail(email, password) {
  const result = await signInWithEmailAndPassword(auth, email, password);
  return result.user;
}

export async function signUpWithEmail(email, password) {
  const result = await createUserWithEmailAndPassword(auth, email, password);
  return result.user;
}

export async function logOut() {
  await signOut(auth);
}

// ── Account deletion ─────────────────────────────────────────────────────────
// EMAIL-DELETION-APPLIED: deletion is handled out-of-band — the user emails a request
// (Settings → "Delete account" opens a pre-filled mail) and we remove their Auth record
// + Firestore doc via the Firebase Console / Admin SDK. Clients have no delete rights
// (firestore.rules: allow delete: if false), so there is no client-side delete here.

export function onAuthChange(callback) {
  return onAuthStateChanged(auth, callback);
}

// ── Firestore user profile ───────────────────────────────────────────────────

export async function getUserProfile(uid) {
  const docRef = doc(db, "users", uid);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return docSnap.data();
  }
  return null;
}

export async function createUserProfile(uid, data) {
  const docRef = doc(db, "users", uid);
  await setDoc(docRef, {
    ...data,
    createdAt: serverTimestamp(),
    lastActive: serverTimestamp(),
  });
}

export async function updateUserProfile(uid, data) {
  const docRef = doc(db, "users", uid);
  await updateDoc(docRef, {
    ...data,
    lastActive: serverTimestamp(),
  });
}

// ── Authenticated fetch ──────────────────────────────────────────────────────
// Every /api/* call from the frontend MUST go through this helper. It attaches
// the current user's Firebase ID token on the Authorization header. Throws
// "Not logged in" if there's no signed-in user so callers can handle the case.
export async function authedFetch(url, options = {}) {
  const user = auth.currentUser;
  if (!user) throw new Error("Not logged in");
  const token = await user.getIdToken();
  return fetch(url, {
    ...options,
    headers: {
      ...(options.headers || {}),
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
}
